import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import DateUtils from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/DateUtils";
import { useResponsive } from "@/hooks/useResponsive";
import WarningSVG from "@/images/inactive-indicator-orange.svg";
import { VirtualDataRoomFile } from "@/models/virtual-data-room/VirtualDataRoomFile";
import { VirtualDataRoomFileNode } from "@/models/virtual-data-room/VirtualDataRoomFileNode";
import { VirtualDataRoomFileProcessingState } from "@/models/virtual-data-room/VirtualDataRoomFileProcessingState";
import { VirtualDataRoomPermissionType } from "@/models/virtual-data-room/VirtualDataroomPermissionType";
import { extractExtensionRemoveDot } from "@/utils/FilenameUtils";
import { isOverflowing } from "@/utils/HTMLElementsUtils";
import { EnumsUtils } from "@/utils/TaskEnumUtils";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Tooltip, Typography, styled } from "@mui/material";
import {
  Row,
  SortDirection,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Link from "next/link";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ActiveIndicatorSVG from "../../../assets/images/active-indicator-green.svg";
import { LazoCheckbox } from "../common/form/LazoCheckbox";
import { FileTableHeaderCellContainer, FileTableMoreHoriz, FilesStyledTable } from "../utils/FileTable/FileTableStyles";
import { FilesIcon } from "../utils/icons/FilesIcon";
import { FolderNotch } from "../utils/icons/FolderNotch";
import OpenInNewIcon from "../utils/icons/OpenInNewIcon";

const columnHelper = createColumnHelper<VirtualDataRoomFile>();

export function fileSizeToHumanReadable(size: number) {
  if (size <= 1000) {
    return size + "B";
  }
  if (size > 1000 && size < 1_000_000) {
    return Math.round(size / 1024) + "KB";
  }
  if (size > 1_000_000) {
    return Math.round(size / (1024 * 1024)) + "MB";
  }
}

function renderNameComponent(
  file: VirtualDataRoomFile,
  fileCb: () => void,
  folderCb: () => void,
  t: (key: string) => string,
  row?: Row<VirtualDataRoomFile>
) {
  let result = <></>;
  const type = file.type;
  const name = file.name;
  const fileCount = file.fileCount || 0;
  const nameContainerRef = useRef<HTMLDivElement>(null);

  switch (type) {
    case "FOLDER":
      result = (
        <NameContainer onClick={folderCb}>
          <LazoCheckbox
            onClick={(e) => e.stopPropagation()}
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
          />
          <FolderNotch sx={{ fill: Colors.LAZO_PURPLE }} />
          <Typography className="folder-typography">{name}</Typography>
          {fileCount > 0 && (
            <Box
              className="count-container"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.125rem",
                "& .MuiSvgIcon-root": {
                  fontSize: "1rem",
                },
              }}
            >
              <FilesIcon sx={{ fontSize: "1rem" }} />
              <Typography fontSize="0.75rem" fontWeight="600">
                {fileCount}
              </Typography>
            </Box>
          )}
        </NameContainer>
      );
      break;
    case "FILE":
      result = (
        <NameContainer>
          <LazoCheckbox
            onClick={(e) => e.stopPropagation()}
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
          />
          <Box display="flex" width="95%" justifyContent="space-between" alignItems="center">
            <Box
              ref={nameContainerRef}
              onClick={fileCb}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "0.25rem",
                width: "85%",
                overflow: "hidden",
                transition: "color 0.1s ease-in",
                "&:hover": {
                  color: Colors.LAZO_PURPLE,
                },
              }}
            >
              <FilesIcon sx={{ fill: Colors.ACCENT_ORANGE, fontSize: "1rem" }} />
              {isOverflowing(nameContainerRef.current) ? (
                <Tooltip title={name}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 400, lineHeight: "19.6px" }}>{name}</Typography>
                </Tooltip>
              ) : (
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 400, lineHeight: "19.6px" }}>{name}</Typography>
              )}
            </Box>
            {renderProcessingState(file.processingState, t)}
          </Box>
        </NameContainer>
      );
      break;
    case "URL":
      result = (
        <NameContainer>
          <LazoCheckbox
            onClick={(e) => e.stopPropagation()}
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
          />
          <OpenInNewIcon sx={{ fontSize: "1rem" }} />
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 400,
              lineHeight: "19.6px",
              "&:hover": {
                color: Colors.LAZO_PURPLE,
              },
            }}
          >
            <Link href={file.fileUrl || "about:blank"} target={"_blank"} rel={"noopener noreferrer"}>
              {name}
            </Link>
          </Typography>
        </NameContainer>
      );
      break;
    default:
      break;
  }
  return result;
}

export function renderProcessingState(pstate: VirtualDataRoomFileProcessingState, t: (key: string) => string) {
  let el = <></>;
  let tooltipText = t("folderSection.processing.summaryAnalysisStatus");
  switch (pstate) {
    case VirtualDataRoomFileProcessingState.PROCESSING_AI_SUMMARY: {
      el = <Spinner size="0.875rem" sx={{ color: Colors.LAZO_PURPLE }} />;
    }
    case VirtualDataRoomFileProcessingState.PROCESSED: {
      el = <img src={ActiveIndicatorSVG.src} alt="check-mark" style={{ width: "20px" }} />;
    }
    case VirtualDataRoomFileProcessingState.CANNOT_BE_ANALYZED: {
      tooltipText = t("folderSection.processing.cannotAnalyze");
      el = <img src={WarningSVG.src} alt="warning" style={{ width: "20px", height: "20px" }} />;
    }
    default:
      break;
  }

  return (
    <Tooltip title={tooltipText}>
      <Box display="flex" alignItems="center" justifyContent="center">
        {el}
      </Box>
    </Tooltip>
  );
}

interface FileDisplayTableProps {
  files: VirtualDataRoomFile[];
  onFileClick: (file: VirtualDataRoomFile) => void;
  onFolderClick: (folder: VirtualDataRoomFileNode) => void;
  onMoreClick: (ev: React.MouseEvent<HTMLElement>, file: VirtualDataRoomFile) => void;
  focusedFileExternalId?: string;
  onClearFocusedFile?: () => void;
  permissions: VirtualDataRoomPermissionType;
  rowSelection: Record<string, boolean>;
  onRowSelectionChange: (selection: Record<string, boolean>) => void;
}

export function FileDisplayTable({
  files,
  onFileClick,
  onFolderClick,
  onMoreClick,
  focusedFileExternalId,
  onClearFocusedFile,
  permissions,
  rowSelection,
  onRowSelectionChange,
}: FileDisplayTableProps) {
  const { t } = useTranslation("virtualDataRooms");
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name-col",
        cell: (info) =>
          renderNameComponent(
            info.row.original,
            () => onFileClick(info.row.original),
            () => onFolderClick(info.row.original as VirtualDataRoomFileNode),
            t,
            info.row
          ),
        header: ({ table }) => (
          <Box sx={{ display: "flex", alignItems: "center", columnGap: "0.5rem" }}>
            {files.some((f) => f.type === "FILE") && (
              <LazoCheckbox
                sx={{
                  "&.MuiButtonBase-root": {
                    padding: "0",
                  },
                }}
                onClick={(e) => e.stopPropagation()}
                checked={table.getIsAllRowsSelected()}
                indeterminate={table.getIsSomeRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
              />
            )}
            <Typography>{t("folderSection.headers.name")}</Typography>
          </Box>
        ),
        meta: {
          width: "60%",
        },
      }),
      columnHelper.accessor("size", {
        id: "size-col",
        cell: (info) => (
          <>
            <Typography> {info.getValue() ? fileSizeToHumanReadable(info.getValue()) : "-"} </Typography>
          </>
        ),
        header: t("folderSection.headers.fileSize"),
      }),
      columnHelper.accessor("type", {
        id: "type-col",
        cell: (info) => (
          <Typography>
            {info.getValue() === "FOLDER"
              ? EnumsUtils.parseEnumToRender(info.getValue())
              : info.getValue() === "URL"
                ? EnumsUtils.parseEnumToRender(info.getValue()).toUpperCase()
                : info.getValue() === "FILE" && info.row.original.name
                  ? extractExtensionRemoveDot(info.row.original.name).toUpperCase()
                  : "-"}
          </Typography>
        ),
        header: t("folderSection.headers.type"),
      }),
      columnHelper.accessor("createdAt", {
        id: "created-at-col",
        cell: (info) => <Typography>{DateUtils.formatDate(new Date(info.getValue()), "MM/DD/YYYY")}</Typography>,
        header: t("folderSection.headers.dateAdded"),
      }),
      {
        id: "externalId",
        accessorKey: "externalId",
        header: () => {},
        cell: () => {},
        meta: {
          width: "0",
        },
      },
    ],
    [files, onFileClick, onFolderClick, t]
  );

  const [sorting, setSorting] = useState<SortingState>([{ id: "type-col", desc: true }]);

  const table = useReactTable({
    data: files,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      rowSelection,
    },
    onRowSelectionChange: onRowSelectionChange,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableRowSelection: true,
  });

  if (isMobileOrTablet) {
    // Mobile Cards View
    return (
      <MobileFilesList>
        {files.map((file) => (
          <MobileFileCard key={file.externalId}>
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center" gap="0.75rem" flex={1} minWidth={0}>
                <LazoCheckbox
                  checked={rowSelection[file.externalId] || false}
                  onChange={(e) => {
                    onRowSelectionChange({
                      ...rowSelection,
                      [file.externalId]: e.target.checked,
                    });
                  }}
                />
                <Box
                  display="flex"
                  alignItems="center"
                  gap="0.5rem"
                  flex={1}
                  minWidth={0}
                  onClick={() => {
                    if (file.type === "FOLDER") {
                      onFolderClick(file as VirtualDataRoomFileNode);
                    } else {
                      onFileClick(file);
                    }
                  }}
                  sx={{ cursor: "pointer" }}
                >
                  {file.type === "FOLDER" ? (
                    <FolderNotch sx={{ fill: Colors.LAZO_PURPLE, flexShrink: 0 }} />
                  ) : file.type === "URL" ? (
                    <OpenInNewIcon sx={{ flexShrink: 0 }} />
                  ) : (
                    <FilesIcon sx={{ fill: Colors.ACCENT_ORANGE, flexShrink: 0 }} />
                  )}
                  <Box flex={1} minWidth={0}>
                    <Typography
                      fontSize="0.875rem"
                      fontWeight={500}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap="1rem">
                      <Typography fontSize="0.75rem" color={Colors.LIGHT_GRAY_8A8E93}>
                        {file.type === "FOLDER"
                          ? `${file.fileCount || 0} files`
                          : file.size
                            ? fileSizeToHumanReadable(file.size)
                            : file.type}
                      </Typography>
                      <Typography fontSize="0.75rem" color={Colors.LIGHT_GRAY_8A8E93}>
                        {DateUtils.formatDate(new Date(file.createdAt), "MM/DD/YY")}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              {![VirtualDataRoomPermissionType.CAN_VIEW, VirtualDataRoomPermissionType.NO_ACCESS].includes(permissions) && (
                <Box onClick={(ev) => onMoreClick(ev, file)} sx={{ padding: "0.5rem", cursor: "pointer" }}>
                  <FileTableMoreHoriz />
                </Box>
              )}
            </Box>
          </MobileFileCard>
        ))}
      </MobileFilesList>
    );
  }

  // Desktop Table View
  return (
    <FilesStyledTable>
      <thead>
        {table.getHeaderGroups().map((group) => (
          <tr key={group.id}>
            {group.headers.map((header) => (
              <th
                key={header.id}
                colSpan={header.colSpan}
                style={{
                  width: header.column.columnDef.meta?.width || "auto",
                  minWidth: header.column.columnDef.meta?.minWidth || "auto",
                }}
              >
                {header.isPlaceholder ? null : (
                  <FileTableHeaderCellContainer onClick={header.column.getToggleSortingHandler()}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() ? (
                      (header.column.getIsSorted() as SortDirection) === "asc" ? (
                        <KeyboardArrowDown />
                      ) : (
                        <KeyboardArrowUp />
                      )
                    ) : null}
                  </FileTableHeaderCellContainer>
                )}
              </th>
            ))}
            {/* Placeholder */}
            <th style={{ width: "40px" }}></th>
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id} onMouseOver={() => focusedFileExternalId === row.original.externalId && onClearFocusedFile?.()}>
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                style={{
                  backgroundColor: focusedFileExternalId === row.original.externalId && Colors.ACCENT_ORANGE_LIGHT,
                }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
            {![VirtualDataRoomPermissionType.CAN_VIEW, VirtualDataRoomPermissionType.NO_ACCESS].includes(permissions) && (
              <td
                style={{
                  backgroundColor: focusedFileExternalId === row.original.externalId && Colors.ACCENT_ORANGE_LIGHT,
                }}
              >
                <Box sx={{ width: "fit-content" }} onClick={(ev) => onMoreClick(ev, row.original)}>
                  <FileTableMoreHoriz />
                </Box>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </FilesStyledTable>
  );
}

const NameContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.25rem",
  minHeight: "2.125rem",
  padding: "0 0.25rem",
  borderRadius: "0.5rem",
  transition: "all 0.1s ease-in",
  ".folder-typography": {
    transition: "all 0.1s ease-in",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: "19.6px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "85%",
  },
  ":hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    cursor: "pointer",
    ".folder-typography": {
      color: Colors.LAZO_PURPLE,
    },
  },
  "& .MuiButtonBase-root": {
    padding: "0",
  },
  "& .MuiTypography-root": {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: "19.6px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "85%",
  },
});

const MobileFilesList = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  width: "100%",
  padding: "0.5rem 0",
});

const MobileFileCard = styled(Box)({
  display: "flex",
  alignItems: "center",
  backgroundColor: Colors.WHITE,
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  borderRadius: "0.5rem",
  padding: "0.75rem",
  minHeight: "4rem",
  transition: "all 0.2s ease-in-out",
  "&:active": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
  },
  "@media (hover: hover)": {
    "&:hover": {
      backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
      borderColor: Colors.LAZO_PURPLE,
    },
  },
});
